import type { Platform, PlatformTheme, PlatformViewport, SharePayload } from './types';
import { subscribeDocumentVisibility } from './visibility';

/**
 * Базовая реализация платформы — работает в обычном браузере и служит
 * фолбэком: если что-то в Telegram/VK-обвязке не поддерживается, поведение
 * должно деградировать сюда, а не ломаться (см. правило в platform-integrations.md).
 */

function currentScheme(): PlatformTheme['scheme'] {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function currentViewport(): PlatformViewport {
  return {
    height: window.visualViewport?.height ?? window.innerHeight,
    isExpanded: true,
    safeAreaInsetBottom: 0,
  };
}

async function fallbackShare(payload: SharePayload): Promise<void> {
  try {
    await navigator.clipboard.writeText(payload.url);
  } catch {
    // Буфер обмена может быть недоступен (нет разрешения, не HTTPS) —
    // ссылка в этом случае просто не скопируется молча.
  }

  if (payload.imageUrl) {
    const link = document.createElement('a');
    link.href = payload.imageUrl;
    link.download = 'portrait.png';
    link.click();
  }
}

export function createWebPlatform(): Platform {
  return {
    kind: 'web',
    accountLinkRole: null,
    paymentKind: null,

    ready() {},

    getIdentity() {
      return null;
    },

    getStartParam() {
      return null;
    },

    getTheme() {
      return { scheme: currentScheme() };
    },

    subscribeTheme(listener) {
      const media = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => listener({ scheme: currentScheme() });

      media.addEventListener('change', handler);
      return () => media.removeEventListener('change', handler);
    },

    // Цвет браузерной полосы ведёт <meta name="theme-color"> (features/theme).
    setChrome() {},

    getViewport() {
      return currentViewport();
    },

    subscribeViewport(listener) {
      const handler = () => listener(currentViewport());

      window.visualViewport?.addEventListener('resize', handler);
      window.addEventListener('resize', handler);

      return () => {
        window.visualViewport?.removeEventListener('resize', handler);
        window.removeEventListener('resize', handler);
      };
    },

    subscribeVisibility: subscribeDocumentVisibility,

    setBackHandler() {
      // У обычной вкладки нет системной кнопки «назад» хоста — навигация
      // работает через роутер и кнопку браузера.
    },

    close() {
      // Веб-вкладку закрывать не наше дело.
    },

    async share(payload) {
      if (navigator.share) {
        try {
          await navigator.share({ url: payload.url, ...(payload.text ? { text: payload.text } : {}) });
          return;
        } catch {
          // Пользователь закрыл системный диалог — это не ошибка приложения.
          return;
        }
      }

      await fallbackShare(payload);
    },

    restart(path) {
      window.location.assign(path);
    },

    openExternalLink(url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    },

    getMessageAccess() {
      return 'unsupported';
    },

    requestMessageAccess() {
      return Promise.resolve(false);
    },

    startPayment() {
      return Promise.resolve('unsupported');
    },
  };
}
